import { bindable, customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  type ActiveElement,
  type ChartEvent,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import template from './camt-analytics-modal.html';
import { getCamtAnalytics } from '../services/api.service';
import type { CamtAnalyticsResponse, DocumentFile } from '../services/api.types';

Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Legend,
  Tooltip,
  zoomPlugin,
);

@customElement({ name: 'camt-analytics-modal', template })
export class CamtAnalyticsModal {
  private readonly i18n: I18N = resolve(I18N);

  @bindable public documentId!: string;
  @bindable public file!: DocumentFile;
  @bindable public closed!: () => void;

  public analytics: CamtAnalyticsResponse | null = null;
  public isLoading = true;
  public errorMessage = '';
  public balanceCanvas!: HTMLCanvasElement;
  public flowCanvas!: HTMLCanvasElement;
  public netChangeCanvas!: HTMLCanvasElement;
  public transactionCanvas!: HTMLCanvasElement;

  private readonly charts: Chart[] = [];
  private loadVersion = 0;
  private keyHandler: ((event: KeyboardEvent) => void) | null = null;

  public attached(): void {
    this.keyHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
      }
    };
    document.addEventListener('keydown', this.keyHandler);
    void this.load();
  }

  public detached(): void {
    if (this.keyHandler) {
      document.removeEventListener('keydown', this.keyHandler);
    }
    this.destroyCharts();
  }

  public documentIdChanged(): void {
    if (this.keyHandler) void this.load();
  }

  public fileChanged(): void {
    if (this.keyHandler) void this.load();
  }

  public close(): void {
    this.closed();
  }

  public zoomIn(): void {
    for (const chart of this.charts) chart.zoom(1.2);
  }

  public zoomOut(): void {
    for (const chart of this.charts) chart.zoom(0.8);
  }

  public resetZoom(): void {
    for (const chart of this.charts) chart.resetZoom();
  }

  public openSourceDocument(point: CamtAnalyticsResponse['months'][number]): void {
    if (!point.sourceDocumentId) return;

    this.closed();
    window.location.href = `/documents?openDocumentId=${encodeURIComponent(point.sourceDocumentId)}`;
  }

  public formatAmount(value: number): string {
    const currency = this.analytics?.currency;
    if (currency) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
      }).format(value);
    }

    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  private async load(): Promise<void> {
    if (!this.documentId || !this.file?.id) return;

    const version = ++this.loadVersion;
    this.isLoading = true;
    this.errorMessage = '';
    this.analytics = null;
    this.destroyCharts();

    try {
      const analytics = await getCamtAnalytics(this.documentId, this.file.id);
      if (version !== this.loadVersion) return;
      this.analytics = analytics;
      requestAnimationFrame(() => {
        if (version === this.loadVersion) this.renderCharts();
      });
    } catch (error: unknown) {
      if (version !== this.loadVersion) return;
      this.errorMessage = error instanceof Error
        ? error.message
        : this.i18n.tr('documents.form.camtAnalytics.loadFailed');
    } finally {
      if (version === this.loadVersion) this.isLoading = false;
    }
  }

  private renderCharts(): void {
    const analytics = this.analytics;
    if (!analytics || analytics.months.length === 0) return;

    this.destroyCharts();
    const labels = analytics.months.map(point => point.month);
    const zoomOptions = {
      pan: { enabled: true, mode: 'xy' as const },
      zoom: {
        wheel: { enabled: true },
        pinch: { enabled: true },
        mode: 'xy' as const,
      },
    };

    if (this.balanceCanvas) {
      this.charts.push(new Chart(this.balanceCanvas, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: this.i18n.tr('documents.form.camtAnalytics.balance'),
            data: analytics.months.map(point => point.hasBalance ? point.balance : null),
            borderColor: '#6366f1',
            backgroundColor: '#6366f1',
            pointRadius: 3,
            tension: 0.25,
            spanGaps: true,
          }],
        },
        options: this.chartOptions(zoomOptions),
      }));
    }

    if (this.flowCanvas) {
      this.charts.push(new Chart(this.flowCanvas, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: this.i18n.tr('documents.form.camtAnalytics.incoming'),
              data: analytics.months.map(point => point.incoming),
              backgroundColor: '#22c55e',
            },
            {
              label: this.i18n.tr('documents.form.camtAnalytics.outgoing'),
              data: analytics.months.map(point => point.outgoing),
              backgroundColor: '#ef4444',
            },
          ],
        },
        options: this.chartOptions(zoomOptions),
      }));
    }

    if (this.netChangeCanvas) {
      this.charts.push(new Chart(this.netChangeCanvas, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: this.i18n.tr('documents.form.camtAnalytics.netChange'),
            data: analytics.months.map(point => point.netChange),
            borderColor: '#f59e0b',
            backgroundColor: '#f59e0b',
            pointRadius: 3,
            tension: 0.25,
          }],
        },
        options: this.chartOptions(zoomOptions),
      }));
    }

    if (this.transactionCanvas) {
      this.charts.push(new Chart(this.transactionCanvas, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: this.i18n.tr('documents.form.camtAnalytics.transactions'),
            data: analytics.months.map(point => point.transactionCount),
            backgroundColor: '#3b82f6',
          }],
        },
        options: this.chartOptions(zoomOptions, true),
      }));
    }
  }

  private chartOptions(
    zoom: {
      pan: { enabled: boolean; mode: 'xy' };
      zoom: {
        wheel: { enabled: boolean };
        pinch: { enabled: boolean };
        mode: 'xy';
      };
    },
    integerAxis = false,
  ) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index' as const, intersect: false },
      onClick: (_event: ChartEvent, elements: ActiveElement[]) => {
        const index = elements[0]?.index;
        const point = index === undefined ? undefined : this.analytics?.months[index];
        if (point) this.openSourceDocument(point);
      },
      plugins: {
        legend: { display: true },
        tooltip: {
          callbacks: {
            label: (item: { dataset: { label?: string }; parsed: { y: number | null } }) =>
              `${item.dataset.label ?? ''}: ${integerAxis
                ? Math.round(item.parsed.y ?? 0)
                : this.formatAmount(item.parsed.y ?? 0)}`,
          },
        },
        zoom,
      },
      scales: {
        x: {
          title: {
            display: true,
            text: this.i18n.tr('documents.form.camtAnalytics.month'),
          },
        },
        y: {
          beginAtZero: integerAxis,
          ticks: {
            precision: integerAxis ? 0 : undefined,
            callback: (value: string | number) => integerAxis
              ? String(Math.round(Number(value)))
              : this.formatAmount(Number(value)),
          },
        },
      },
    };
  }

  private destroyCharts(): void {
    for (const chart of this.charts) chart.destroy();
    this.charts.length = 0;
  }
}
